/**
 * Contracts Layout — h-screen flex column: AppBar header, the six-tab nav,
 * the global backtest banner (plan §11, G12), then the scrolling routed
 * content, same shell shape as Insights/Site Organizer.
 *
 * TabNav and BacktestBanner are hoisted HERE (rendered once per module
 * mount) rather than inside each tab screen — the same "Fix Spec C" lesson
 * InsightsApp already documents for its own ProtectedRoute+Layout hoist:
 * putting them inside every placeholder instead would remount the tab nav
 * and re-fire the health check on every tab switch, and plan §11 calls the
 * banner "Global" (one banner for the module, not one per tab).
 *
 * Rendered only once ContractsApp's ProtectedRoute has confirmed a session,
 * the entitlement, and a selected site — so `useContracts().siteId` here is
 * never read before it can be non-null for a non-super_admin user.
 */
import { ReactNode } from 'react';
import { Header } from './Header';
import { TabNav } from '../TabNav';
import { BacktestBanner } from '../BacktestBanner';
import { useContracts } from '../../ContractsProvider';
import { APP_VERSION, BUILD_DATE, COPYRIGHT_YEAR, COMPANY_NAME } from '../../../config/version';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { siteId } = useContracts();

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      <Header />
      <TabNav />
      <BacktestBanner siteId={siteId} />
      <main className="flex-1 overflow-auto">{children}</main>
      <footer className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-2 px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 dark:text-gray-400 gap-1">
          <p>&copy; {COPYRIGHT_YEAR} {COMPANY_NAME}. All rights reserved.</p>
          <p>Version {APP_VERSION} &middot; {BUILD_DATE}</p>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
